import { Button } from '@/ui/shadcn/button';
import type { ComposeAiDraftPlaceProps } from '../../lib/email/composeEmail';
import { Icon } from '../../ui/icons/Icon';
import styles from './ComposeAiDraftPlaceholder.module.css';

/**
 * Whether the compose dialog has something to draft from: an origin or a message being answered (F11-h spec
 * decision 8). *AI draft* and its stand-in render only then, so a new email from the inbox shows neither.
 */
export function hasDraftContext(
    origin: ComposeAiDraftPlaceProps['origin'],
    sourceMessageId: ComposeAiDraftPlaceProps['sourceMessageId'],
): boolean {
    return origin !== null || sourceMessageId !== null;
}

/**
 * What the compose dialog's `aiDraft` place shows while the *AI draft* button's chunk loads (F11-h WI-1.2 plan
 * `## Notes`, fix-2): the same shadcn `Button`, hidden and inert, so the button's room is held from the
 * dialog's first paint. The toolbar's end group is right-aligned, so a button that appeared later would move
 * *Insert template* left; when it appeared during a press, the press ended outside *Insert template* and
 * opened nothing. Kept out of the button's chunk (`src/app/composeEmailComposition.ts` imports it directly),
 * and holds no logic: it never takes focus, a press or a screen reader's attention.
 */
export function ComposeAiDraftPlaceholder({ origin, sourceMessageId }: ComposeAiDraftPlaceProps) {
    if (!hasDraftContext(origin, sourceMessageId)) {
        return null;
    }
    return (
        <div
            aria-hidden="true"
            className={styles.placeholder}
            data-testid="ai-drafts-compose-placeholder"
            inert
        >
            <Button
                data-testid="ai-drafts-compose-placeholder-button"
                size="sm"
                tabIndex={-1}
                variant="outline"
            >
                <Icon name="aiDraft" size="sm" />
                AI draft
            </Button>
        </div>
    );
}
